import { useEffect, useState } from 'react'

interface Size {
  width: number
  height: number
}

// ResizeObserver delivers size updates after the browser's layout pass, so
// there's no forced reflow. The previous implementation read offsetWidth /
// offsetHeight inside hook deps that re-evaluated on every render — each read
// forced a synchronous layout flush, which with ~20 widgets re-rendering at
// ~40 Hz produced thousands of reflows per second.
export const useElementSize = <T extends HTMLElement = HTMLDivElement>(): [
  (node: T | null) => void,
  Size
] => {
  const [ref, setRef] = useState<T | null>(null)
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  useEffect(() => {
    if (ref === null) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry === undefined) return
      const { width, height } = entry.contentRect
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
    })

    observer.observe(ref)

    return () => observer.disconnect()
  }, [ref])

  return [setRef, size]
}
